import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, FileJson, Loader2, Upload } from 'lucide-react';
import { auth } from '../firebase';
import {
  bulkUpdateModelsAdmin,
  listModelsAdmin,
  saveModelAdmin,
  deleteModelAdmin,
  setModelWorkEnabled,
} from '../utils/adminModelsApi';
import { useModels } from '../contexts/ModelContext';
import AdminLayout from '../components/admin/AdminLayout';

const EXPORT_STATUS_TIMEOUT_MS = 3500;
const MODEL_EXPORT_COLUMNS = [
  'id',
  'name',
  'modelString',
  'provider',
  'displayProvider',
  'status',
  'workEnabled',
  'sortOrder',
  'contextWindow',
  'maxTokens',
  'tokenLimitEnabled',
  'dailyTokenLimit',
  'platforms',
  'supportedInputs',
  'online',
  'thinking',
  'reasoningLevels',
  'deep_research',
  'isPremium',
  'isNew',
  'isOld',
  'description',
  'subtitle',
  'symbol',
  'color',
  'live',
];

const panelClass =
  'border border-gray-200 bg-white text-gray-900 rounded p-3 space-y-2';
const inputClass =
  'w-full border border-gray-300 rounded bg-white p-2 text-gray-900 placeholder:text-gray-400';
const labelClass = 'block text-sm text-gray-700';
const REASONING_LEVEL_OPTIONS = [
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
];

const emptyModel = {
  id: '',
  name: '',
  modelString: '',
  provider: '',
  displayProvider: '',
  subtitle: '',
  description: '',
  symbol: 'sparkles',
  color: '#808080',
  status: 'active',
  platforms: ['web', 'ios'],
  sortOrder: 0,
  contextWindow: '',
  maxTokens: '',
  isPremium: false,
  tokenLimitEnabled: false,
  dailyTokenLimit: '',
  supportedInputs: [],
  isNew: false,
  isOld: false,
  online: false,
  thinking: false,
  reasoningLevels: [],
  deep_research: false,
};

const getDateKeyFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const sanitizeFilePart = (value) =>
  String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  let text = Array.isArray(value) ? value.join('|') : String(value);
  if (/^[=+\-@]/.test(text)) {
    text = `'${text}`;
  }
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const resolveTokenLimitEnabled = (model) =>
  model.tokenLimitEnabled === true ||
  model.token_limit_enabled === true ||
  model.tokenLimitEnabled === 'true' ||
  model.token_limit_enabled === 'true';

const resolveDailyTokenLimit = (model) =>
  model.dailyTokenLimit ??
  model.daily_token_limit ??
  model.tokenLimitPerDay ??
  model.token_limit_per_day ??
  '';

const buildModelExportRows = (models, liveModelStrings) =>
  models.map((model) => {
    const contextWindow = model.contextWindow ?? model.maxTokens ?? model.max_tokens ?? '';
    const maxTokens = model.maxTokens ?? model.max_tokens ?? contextWindow ?? '';
    return {
      id: model.id || '',
      name: model.name || '',
      modelString: model.modelString || '',
      provider: model.provider || '',
      displayProvider: model.displayProvider || '',
      status: model.status || '',
      sortOrder: model.sortOrder ?? '',
      contextWindow,
      maxTokens,
      tokenLimitEnabled: resolveTokenLimitEnabled(model),
      dailyTokenLimit: resolveDailyTokenLimit(model),
      platforms: model.platforms || [],
      supportedInputs: model.supportedInputs || [],
      online: Boolean(model.online),
      thinking: Boolean(model.thinking),
      reasoningLevels: Array.isArray(model.reasoningLevels) ? model.reasoningLevels : [],
      deep_research: Boolean(model.deep_research),
      isPremium: Boolean(model.isPremium),
      isNew: Boolean(model.isNew),
      isOld: Boolean(model.isOld),
      description: model.description || '',
      subtitle: model.subtitle || '',
      symbol: model.symbol || '',
      color: model.color || '',
      workEnabled: model.workEnabled === true,
      live: liveModelStrings.has(model.modelString || model.id),
    };
  });

const buildModelsCsv = (rows) => [
  MODEL_EXPORT_COLUMNS.join(','),
  ...rows.map((row) =>
    MODEL_EXPORT_COLUMNS.map((column) => escapeCsvValue(row[column])).join(',')
  ),
].join('\n');

const AdminModelsPage = () => {
  const { models: liveModels } = useModels();
  const exportStatusTimeoutRef = useRef(null);
  const importFileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);
  const [form, setForm] = useState(emptyModel);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [workSaving, setWorkSaving] = useState(new Set());

  const fetchAdminModels = async () => {
    setError(null);
    setExportStatus(null);
    setLoading(true);
    try {
      const data = await listModelsAdmin();
      setModels(data.models || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAdmin = async () => {
      const user = auth.currentUser;
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      const token = await user.getIdTokenResult();
      const adminClaim = token?.claims?.admin === true;
      setIsAdmin(adminClaim);
      if (adminClaim) {
        await fetchAdminModels();
      } else {
        setLoading(false);
      }
    };
    checkAdmin().catch((err) => {
      setError(err.message);
      setLoading(false);
    });
  }, []);

  useEffect(() => () => {
    if (exportStatusTimeoutRef.current) {
      window.clearTimeout(exportStatusTimeoutRef.current);
    }
  }, []);

  const handleSelect = (model, asCopy = false) => {
    if (!model) {
      setForm(emptyModel);
      return;
    }

    const resolvedContextWindow =
      model.contextWindow ?? model.maxTokens ?? model.max_tokens ?? '';

    const resolvedMaxTokens =
      model.maxTokens ?? model.max_tokens ?? resolvedContextWindow ?? '';

    const base = {
      ...emptyModel,
      ...model,
      id: model.id || model.modelString || '',
      contextWindow: resolvedContextWindow,
      maxTokens: resolvedMaxTokens,
      tokenLimitEnabled: resolveTokenLimitEnabled(model),
      dailyTokenLimit: resolveDailyTokenLimit(model),
      platforms: model.platforms || ['web', 'ios'],
      supportedInputs: model.supportedInputs || [],
      reasoningLevels: Array.isArray(model.reasoningLevels) ? model.reasoningLevels : [],
    };
    if (asCopy) {
      base.id = '';
      base.modelString = '';
      base.name = `${base.name} (copy)`;
      base.status = 'active';
    }
    setForm(base);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleArrayChange = (key, value) => {
    const arr = value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    setForm((prev) => ({ ...prev, [key]: arr }));
  };

  const handleReasoningLevelToggle = (level, enabled) => {
    setForm((prev) => {
      const selected = new Set(prev.reasoningLevels || []);
      if (enabled) selected.add(level);
      else selected.delete(level);

      return {
        ...prev,
        reasoningLevels: REASONING_LEVEL_OPTIONS.filter((value) => selected.has(value)),
      };
    });
  };

  const handleSave = async () => {
    if (!form.id) {
      setError('ID is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const toNumberOrNull = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const modelForm = { ...form };
      delete modelForm.workEnabled; // The row checkbox saves this independently.
      delete modelForm.favorite;
      delete modelForm.isFavorite;
      delete modelForm.inputPricePerMillionTokens;
      delete modelForm.outputPricePerMillionTokens;
      delete modelForm.priceCurrency;

      const payload = {
        ...modelForm,
        sortOrder: Number(form.sortOrder) || 0,
        contextWindow: toNumberOrNull(form.contextWindow),
        maxTokens: toNumberOrNull(form.maxTokens),
        tokenLimitEnabled: !!form.tokenLimitEnabled,
        dailyTokenLimit: toNumberOrNull(form.dailyTokenLimit),
      };
      await saveModelAdmin(payload);
      await fetchAdminModels();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleWorkToggle = async (model, enabled) => {
    setWorkSaving(previous => new Set(previous).add(model.id));
    setError(null);
    try {
      await setModelWorkEnabled(model.id, enabled);
      setModels(previous => previous.map(item => item.id === model.id ? { ...item, workEnabled: enabled } : item));
      setForm(previous => previous.id === model.id ? { ...previous, workEnabled: enabled } : previous);
    } catch (err) {
      setError(`Could not update Work availability: ${err.message}`);
    } finally {
      setWorkSaving(previous => { const next = new Set(previous); next.delete(model.id); return next; });
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteModelAdmin(form.id);
      await fetchAdminModels();
      setForm(emptyModel);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const showTransientStatus = (message) => {
    setExportStatus(message);

    if (exportStatusTimeoutRef.current) {
      window.clearTimeout(exportStatusTimeoutRef.current);
    }
    exportStatusTimeoutRef.current = window.setTimeout(() => {
      setExportStatus(null);
      exportStatusTimeoutRef.current = null;
    }, EXPORT_STATUS_TIMEOUT_MS);
  };

  const handleImportModelsClick = () => {
    if (importing) return;
    importFileInputRef.current?.click();
  };

  const handleImportModelsFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setError(null);
    setExportStatus(null);

    try {
      const text = await file.text();
      let parsed;

      try {
        parsed = JSON.parse(text);
      } catch (_error) {
        throw new Error('Upload a valid JSON models export.');
      }

      const importedModels = Array.isArray(parsed) ? parsed : parsed?.models;
      if (!Array.isArray(importedModels)) {
        throw new Error('JSON must be a model array or an exported object with a models array.');
      }
      if (importedModels.length === 0) {
        throw new Error('No models found in the uploaded JSON.');
      }

      const result = await bulkUpdateModelsAdmin(importedModels);
      await fetchAdminModels();
      setForm(emptyModel);

      const updatedCount = result.updatedCount ?? importedModels.length;
      showTransientStatus(
        `Imported ${updatedCount} model${updatedCount === 1 ? '' : 's'} from ${file.name}.`
      );
    } catch (err) {
      setError(err.message || 'Failed to import models.');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleExportModels = (format) => {
    if (exportingFormat) return;

    setError(null);
    setExportStatus(null);
    setExportingFormat(format);

    try {
      const generatedAt = new Date().toISOString();
      const rows = buildModelExportRows(sortedModels, liveModelStrings);
      const exportDate = getDateKeyFromDate(new Date());
      const filename = `arrowai-models-${sanitizeFilePart(exportDate)}.${format}`;
      const content = format === 'json'
        ? JSON.stringify(
            {
              schemaVersion: 1,
              generatedAt,
              summary: {
                totalModels: rows.length,
                limitedModels: rows.filter(
                  (row) =>
                    row.tokenLimitEnabled === true &&
                    Number(row.dailyTokenLimit) > 0
                ).length,
              },
              models: rows,
            },
            null,
            2
          )
        : buildModelsCsv(rows);
      const type = format === 'json'
        ? 'application/json;charset=utf-8'
        : 'text/csv;charset=utf-8';

      downloadBlob(new Blob([content], { type }), filename);
      showTransientStatus(`Models ${format.toUpperCase()} downloaded.`);
    } catch (err) {
      setError(err.message || 'Failed to export models.');
    } finally {
      setExportingFormat(null);
    }
  };

  const liveModelStrings = useMemo(
    () => new Set((liveModels || []).map((m) => m.modelString)),
    [liveModels]
  );

  const sortedModels = useMemo(
    () =>
      [...models].sort((a, b) => {
        const aOrder = Number(a.sortOrder) || 0;
        const bOrder = Number(b.sortOrder) || 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      }),
    [models]
  );

  if (!auth.currentUser) {
    return (
      <AdminLayout>
        <div className="p-6 text-red-600">Sign in to access admin tools.</div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 text-gray-600">Loading...</div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-6 text-red-600">You need admin access to manage models.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <header className="z-10 shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Model Admin</h1>
          <p className="text-xs text-gray-500">Create, edit, and manage the models exposed to users.</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => handleExportModels('csv')}
            disabled={loading || Boolean(exportingFormat)}
            title="Download models CSV"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {exportingFormat === 'csv' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Download size={15} />
            )}
            <span>CSV</span>
          </button>
          <button
            type="button"
            onClick={() => handleExportModels('json')}
            disabled={loading || Boolean(exportingFormat)}
            title="Download models JSON"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {exportingFormat === 'json' ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <FileJson size={15} />
            )}
            <span>JSON</span>
          </button>
          <input
            ref={importFileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportModelsFile}
          />
          <button
            type="button"
            onClick={handleImportModelsClick}
            disabled={loading || importing || Boolean(exportingFormat)}
            title="Import models JSON"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {importing ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Upload size={15} />
            )}
            <span>Import JSON</span>
          </button>
          <button
            className="h-9 rounded-md bg-blue-600 px-3 text-sm text-white hover:bg-blue-700"
            onClick={() => setForm(emptyModel)}
          >
            New Model
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 bg-gray-50 px-6 py-6 text-gray-900">
        <div className="flex h-full min-h-0 flex-col gap-4">
          {error && <div className="shrink-0 rounded bg-red-100 p-2 text-red-700">{error}</div>}
          {exportStatus && (
            <div className="inline-flex shrink-0 items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              <Check size={16} />
              <span>{exportStatus}</span>
            </div>
          )}

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-2 gap-4 md:grid-cols-3 md:grid-rows-1">
        <div className={`md:col-span-2 min-h-0 flex flex-col ${panelClass}`}>
          <h2 className="font-semibold">Models (admin view)</h2>
          <p className="text-xs text-gray-500">Work checkboxes save immediately and control the macOS Work model picker independently of Chat. Reconnect Work to refresh its picker.</p>
          <div className="min-h-0 flex-1 overflow-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-[1] bg-gray-100 text-gray-700">
                <tr>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">ID</th>
                  <th className="p-2 text-left">Sort Order</th>
                  <th className="p-2 text-left">Provider</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Context Window</th>
                  <th className="p-2 text-left">Max Tokens</th>
                  <th className="p-2 text-left">Daily Token Limit</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-center">Work</th>
                  <th className="p-2 text-left">Platforms</th>
                  <th className="p-2 text-left">Reasoning Levels</th>
                  <th className="p-2 text-left">Live?</th>
                </tr>
              </thead>
              <tbody>
                {sortedModels.map((m) => {
                  const workSupported = ['openai', 'anthropic', 'google', 'gemini'].includes((m.provider || '').toLowerCase());
                  const contextWindow = m.contextWindow ?? m.maxTokens ?? m.max_tokens;
                  const maxTokens = m.maxTokens ?? m.max_tokens ?? contextWindow;
                  const tokenLimitEnabled = resolveTokenLimitEnabled(m);
                  const dailyTokenLimit = resolveDailyTokenLimit(m);

                  return (
                    <tr
                      key={m.id}
                      className="cursor-pointer border-b border-gray-100 text-gray-800 hover:bg-gray-50 last:border-b-0"
                      onClick={() => handleSelect(m)}
                    >
                      <td className="p-2">{m.name}</td>
                      <td className="p-2">{m.id}</td>
                      <td className="p-2">{m.sortOrder ?? '—'}</td>
                      <td className="p-2">{m.provider}</td>
                      <td className="p-2 max-w-[220px] truncate">{m.description || '—'}</td>
                      <td className="p-2">{contextWindow ?? '—'}</td>
                      <td className="p-2">{maxTokens ?? '—'}</td>
                      <td className="p-2 text-xs">
                        {tokenLimitEnabled && Number(dailyTokenLimit) > 0 ? (
                          <div className="space-y-0.5">
                            <div className="font-medium text-green-700">Enabled</div>
                            <div>{Number(dailyTokenLimit).toLocaleString()} tokens/day</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Off</span>
                        )}
                      </td>
                      <td className="p-2">{m.status}</td>
                      <td className="p-2 text-center" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Enable ${m.name || m.id} for Work`}
                          title={workSupported ? 'Available in macOS Work' : 'This provider is not connected to Work'}
                          className="h-4 w-4 cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                          checked={m.workEnabled === true}
                          disabled={workSaving.has(m.id) || (!workSupported && !m.workEnabled)}
                          onChange={(event) => handleWorkToggle(m, event.target.checked)}
                        />
                        {workSaving.has(m.id) && <Loader2 aria-label="Saving Work setting" className="inline ml-1 h-3 w-3 animate-spin" />}
                      </td>
                      <td className="p-2">{(m.platforms || []).join(', ')}</td>
                      <td className="p-2 text-xs">
                        {Array.isArray(m.reasoningLevels) && m.reasoningLevels.length > 0
                          ? m.reasoningLevels.join(', ')
                          : 'App fallback'}
                      </td>
                      <td className="p-2">
                        {liveModelStrings.has(m.modelString || m.id) ? '✅' : '—'}
                      </td>
                      <td className="p-2 text-right">
                        <button
                          className="text-blue-600 text-xs underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(m, true);
                          }}
                        >
                          Copy
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className={`${panelClass} min-h-0 overflow-auto`}>
          <h2 className="font-semibold">Edit / Create</h2>
          <label className={labelClass}>ID (doc id)</label>
          <input
            className={inputClass}
            value={form.id}
            onChange={(e) => handleChange('id', e.target.value)}
          />

          <label className={labelClass}>Name</label>
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />

          <label className={labelClass}>Model String</label>
          <input
            className={inputClass}
            value={form.modelString}
            onChange={(e) => handleChange('modelString', e.target.value)}
          />

          <label className={labelClass}>Provider</label>
          <input
            className={inputClass}
            value={form.provider}
            onChange={(e) => handleChange('provider', e.target.value)}
          />

          <label className={labelClass}>Display Provider (optional)</label>
          <input
            className={inputClass}
            value={form.displayProvider}
            onChange={(e) => handleChange('displayProvider', e.target.value)}
            placeholder="Defaults to Provider"
          />

          <label className={labelClass}>Subtitle</label>
          <input
            className={inputClass}
            value={form.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
          />

          <label className={labelClass}>Description</label>
          <textarea
            className={`${inputClass} min-h-[84px]`}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Short model description shown in the picker"
          />

          <label className={labelClass}>Symbol</label>
          <input
            className={inputClass}
            value={form.symbol}
            onChange={(e) => handleChange('symbol', e.target.value)}
            placeholder="e.g. sparkles, bolt, crown"
          />

          <label className={labelClass}>Color (hex)</label>
          <input
            className={inputClass}
            value={form.color}
            onChange={(e) => handleChange('color', e.target.value)}
          />

          <label className={labelClass}>Status</label>
          <select
            className={inputClass}
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            <option value="active">active</option>
            <option value="hidden">hidden</option>
            <option value="deprecated">deprecated</option>
          </select>

          <label className={labelClass}>Platforms (comma)</label>
          <input
            className={inputClass}
            value={(form.platforms || []).join(', ')}
            onChange={(e) => handleArrayChange('platforms', e.target.value)}
          />

          <label className={labelClass}>Supported Inputs (MIME, comma)</label>
          <input
            className={inputClass}
            value={(form.supportedInputs || []).join(', ')}
            onChange={(e) => handleArrayChange('supportedInputs', e.target.value)}
          />

          <fieldset className="rounded border border-gray-200 p-3">
            <legend className="px-1 text-sm text-gray-700">Reasoning Levels</legend>
            <p className="mb-2 text-xs text-gray-500">
              Select the exact effort values supported by this model. These database values
              override the built-in web, iOS, and macOS compatibility fallbacks.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {REASONING_LEVEL_OPTIONS.map((level) => (
                <label key={level} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(form.reasoningLevels || []).includes(level)}
                    onChange={(event) => handleReasoningLevelToggle(level, event.target.checked)}
                  />
                  <span>{level}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Leave every value unchecked to use the app fallback.
            </p>
          </fieldset>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>Context Window (tokens)</label>
              <input
                type="number"
                className={inputClass}
                value={form.contextWindow}
                onChange={(e) => handleChange('contextWindow', e.target.value)}
                min="0"
              />
            </div>
            <div>
              <label className={labelClass}>Max Tokens</label>
              <input
                type="number"
                className={inputClass}
                value={form.maxTokens}
                onChange={(e) => handleChange('maxTokens', e.target.value)}
                min="0"
              />
            </div>
            <div>
              <label className={labelClass}>Daily Token Limit</label>
              <input
                type="number"
                className={inputClass}
                value={form.dailyTokenLimit}
                onChange={(e) => handleChange('dailyTokenLimit', e.target.value)}
                min="0"
                placeholder="e.g. 100000"
              />
            </div>
            <div>
              <label className={labelClass}>Sort Order</label>
              <input
                type="number"
                className={inputClass}
                value={form.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.tokenLimitEnabled}
                onChange={(e) => handleChange('tokenLimitEnabled', e.target.checked)}
              />
              <span>Enable Daily Token Limit</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.isPremium}
                onChange={(e) => handleChange('isPremium', e.target.checked)}
              />
              <span>Premium</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.isNew}
                onChange={(e) => handleChange('isNew', e.target.checked)}
              />
              <span>New</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.isOld}
                onChange={(e) => handleChange('isOld', e.target.checked)}
              />
              <span>Old</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.online}
                onChange={(e) => handleChange('online', e.target.checked)}
              />
              <span>Online</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.thinking}
                onChange={(e) => handleChange('thinking', e.target.checked)}
              />
              <span>Thinking</span>
            </label>
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.deep_research}
                onChange={(e) => handleChange('deep_research', e.target.checked)}
              />
              <span>Deep Research</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              className="flex-1 px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="px-3 py-2 rounded bg-red-600 text-white disabled:opacity-50"
              onClick={handleDelete}
              disabled={deleting || !form.id}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
        </div>
      </main>
    </AdminLayout>
  );
};

export default AdminModelsPage;
